import React, {useState} from 'react';
import {LocalizationProvider, TimePicker, DatePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import config from '../config';
import Helper from "../helpers";

const baseUrl = config.apiBaseUrl;

const AddSession = (props) => {
    const {handleClose, Values} = props;
    const [sessions, setSessions] = useState([]);
    const [sessionName, setSessionName] = useState('');
    const [sessionDate, setSessionDate] = useState(null);
    const [sessionTime, setSessionTime] = useState(dayjs().add(1, 'hour'));
    const [sessionSeats, setSessionSeats] = useState(1);
    const [sessionLength, setSessionLength] = useState(1);
    const [sessionTokenCost, setSessionTokenCost] = useState(1);

    const handleAddSession = () => {
        if (sessions.length >= 5) {
            alert('You can only add up to 5 sessions at a time.');
            return;
        }

        const newSession = {
            name: sessionName,
            date: sessionDate,
            time: sessionTime || dayjs().add(1, 'hour'),
            length: sessionLength,
            seats: sessionSeats,
            tokenCost: sessionTokenCost
        };

        setSessions([...sessions, newSession]);
        setSessionName('');
        setSessionDate(null);
        setSessionTime(dayjs().add(1, 'hour'));
        setSessionLength(1);
        setSessionSeats(1);
        setSessionTokenCost(1);
    };

    const handleRemoveSession = (index) => {
        const updatedSessions = sessions.filter((_, i) => i !== index);
        setSessions(updatedSessions);
    };

    const handleSubmit = async () => {
        const sessionData =  sessions.map(session => ({
                // No trailing Z: this is a local wall-clock time, not UTC (see BulkClass.js for
                // the full explanation of the display-time drift this used to cause).
                startDate: `${session.date.format('YYYY-MM-DD')}T${session.time.format('HH:mm:00')}`,
                availableSeats: session.seats,
                numberOfMinuets: session.length * 60,
                name: session.name,
                tokenCost: session.tokenCost,
            }));
        const token = await Helper.getCookie('wh-jwt');
        fetch(`${baseUrl}/api/Sessions/AddSession/${Values.venue.key}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(sessionData),
        })
            .then(response => {
                if (response.ok) {
                    alert('Sessions added successfully');
                    handleClose();
                } else {
                    alert('Failed to add sessions');
                }
            })
            .catch(error => {
                console.error('Error adding sessions', error);
                alert('Failed to add sessions');
            });
    };


    return (
        <div className="p-6 sm:px-1">
            <h2 className="text-2xl font-bold text-white mb-6">Add Session</h2>
            
            <div className="space-y-4 mb-6">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Session Date
                            </label>
                            <DatePicker
                                value={sessionDate}
                                format="DD/MM/YYYY"
                                onChange={(newValue) => setSessionDate(newValue)}
                                slotProps={{
                                    textField: {
                                        className: "input-field",
                                        fullWidth: true
                                    }
                                }}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Session Start Time
                            </label>
                            <TimePicker
                                value={sessionTime}
                                onChange={(newValue) => setSessionTime(newValue)}
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        className: "input-field",
                                        fullWidth: true
                                    }
                                }}
                            />
                        </div>
                    </div>
                </LocalizationProvider>

                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Session Name
                    </label>
                    <input
                        type="text"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        className="input-field"
                        placeholder="Enter session name"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Session Length (hours)
                        </label>
                        <input
                            type="number"
                            value={sessionLength}
                            onChange={(e) => setSessionLength(e.target.value)}
                            className="input-field"
                            min="1"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Available Seats
                        </label>
                        <input
                            type="number"
                            value={sessionSeats}
                            onChange={(e) => setSessionSeats(e.target.value)}
                            className="input-field"
                            min="1"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Token Cost
                        </label>
                        <input
                            type="number"
                            value={sessionTokenCost}
                            onChange={(e) => setSessionTokenCost(e.target.value)}
                            className="input-field"
                            min="1"
                        />
                    </div>
                </div>

                <div className="flex space-x-3">
                    <button
                        onClick={handleAddSession}
                        className="btn-primary flex items-center"
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Plan Session
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                        Submit All Sessions
                    </button>
                </div>
            </div>

            {/* Planned Sessions List */}
            {sessions.length > 0 && (
                <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-white">Planned Sessions</h3>
                    {sessions.map((session, index) => (
                        <div key={index} className="card p-4 flex items-center justify-between">
                            <div className="flex-1">
                                <div className="font-medium text-white">
                                    {session.name}
                                </div>
                                <div className="text-sm text-gray-400">
                                    {session.date.format('YYYY-MM-DD')} at {session.time.format('HH:mm')} ({session.length} hours)
                                </div>
                                <div className="text-sm text-gray-400">
                                    Seats: {session.seats} | Cost: {session.tokenCost} tokens
                                </div>
                            </div>
                            <button
                                onClick={() => handleRemoveSession(index)}
                                className="ml-4 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AddSession;
